import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Plus } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { useSeo } from "../hooks/useSeo";
import Marquee from "../components/Marquee";
import CasesPreview from "../components/CasesPreview";

const HERO_IMG = "https://images.unsplash.com/photo-1680416124510-5eae1beca412?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwbW9kZXJuJTIwYXBhcnRtZW50JTIwaW50ZXJpb3IlMjBsdXh1cnl8ZW58MHx8fHwxNzc5NzI2NDI3fDA&ixlib=rb-4.1.0&q=85";
const EXPAT_IMG = "https://images.unsplash.com/photo-1459679749680-18eb1eb37418?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxhbXN0ZXJkYW0lMjBjYW5hbCUyMGhvdXNlcyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3Nzk3MjY0Mjd8MA&ixlib=rb-4.1.0&q=85";
const BIZ_IMG = "https://images.pexels.com/photos/13219418/pexels-photo-13219418.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

/* ---------------- Hero — editorial, no-card ---------------- */
function Hero() {
  const { t } = useI18n();
  return (
    <section className="relative" data-testid="hero-section">
      <div className="container-wide pt-10 md:pt-16 pb-20 md:pb-28">
        <div className="grid lg:grid-cols-12 gap-x-10 gap-y-10 items-end">
          <div className="lg:col-span-12 flex items-center justify-between gap-6">
            <p className="eyebrow" data-testid="hero-eyebrow">{t.home.eyebrow}</p>
            <p className="hidden md:block text-xs uppercase tracking-[0.22em] text-muted-foreground font-bold">
              {t.editorial.estLabel}
            </p>
          </div>

          <h1
            className="lg:col-span-12 text-[40px] leading-[0.98] tracking-[-0.025em] font-medium text-foreground sm:text-[60px] lg:text-[112px] xl:text-[128px]"
            data-testid="hero-title"
          >
            {(() => {
              const title = t.home.heroTitle;
              const idx = title.indexOf("Benelux");
              if (idx === -1) return title;
              const before = title.slice(0, idx);
              const after = title.slice(idx + "Benelux".length);
              return (
                <>
                  {before}
                  <span className="italic text-primary" style={{ fontFamily: '"Cabinet Grotesk", serif' }}>
                    Benelux
                  </span>
                  {after}
                </>
              );
            })()}
          </h1>

          <div className="lg:col-span-7">
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed" data-testid="hero-sub">
              {t.home.heroSub}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/contact?intent=intake" className="btn-primary" data-testid="hero-cta-primary">
                {t.nav.requestSearch}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/contact?intent=intake" className="btn-ghost" data-testid="hero-cta-secondary">
                {t.nav.bookIntake}
              </Link>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="border-t border-border pt-6 space-y-4 text-sm text-foreground/90 max-w-md ml-auto">
              <div className="flex items-baseline justify-between gap-6">
                <span className="font-display text-2xl text-foreground">2–4</span>
                <span className="text-muted-foreground text-xs uppercase tracking-[0.18em] text-right max-w-[180px]">{t.editorial.heroStat1Label}</span>
              </div>
              <div className="flex items-baseline justify-between gap-6 border-t border-border pt-4">
                <span className="font-display text-2xl text-foreground">BE · NL · LU</span>
                <span className="text-muted-foreground text-xs uppercase tracking-[0.18em] text-right max-w-[180px]">{t.editorial.heroStat2Label}</span>
              </div>
              <div className="flex items-baseline justify-between gap-6 border-t border-border pt-4">
                <span className="font-display text-2xl text-foreground">NL · EN · FR</span>
                <span className="text-muted-foreground text-xs uppercase tracking-[0.18em] text-right max-w-[180px]">{t.editorial.heroStat3Label}</span>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Wide editorial image strip */}
      <div className="container-wide pb-12 md:pb-20">
        <div className="relative overflow-hidden border-y border-border">
          <img
            src={HERO_IMG}
            alt="Premium interieur Tip Top Property"
            className="w-full h-[42vh] md:h-[58vh] object-cover"
            loading="eager"
          />
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-background/40 to-transparent pointer-events-none" />
          <div className="absolute top-5 left-5 text-[10px] uppercase tracking-[0.25em] font-bold text-white/90 bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-sm">
            Fig. 01 — Studio interior, private client, Luxembourg
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Practice / Manifesto block ---------------- */
function Manifesto() {
  const { t } = useI18n();
  return (
    <section className="border-t border-border" data-testid="manifesto-section">
      <div className="container-wide py-20 md:py-28 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-3">
          <p className="eyebrow">{t.editorial.manifestoEyebrow}</p>
        </div>
        <div className="lg:col-span-9">
          <p
            className="font-display text-2xl md:text-3xl lg:text-[40px] leading-[1.15] tracking-[-0.015em] text-foreground"
          >
            {t.editorial.manifestoBodyBefore}<span className="italic text-primary">{t.editorial.manifestoHighlight}</span>{t.editorial.manifestoBodyAfter}
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Editorial numbered benefits ---------------- */
function Benefits() {
  const { t } = useI18n();
  const items = [
    { title: t.home.benefit1Title, body: t.home.benefit1Body, tag: t.editorial.benefitsTags[0] },
    { title: t.home.benefit2Title, body: t.home.benefit2Body, tag: t.editorial.benefitsTags[1] },
    { title: t.home.benefit3Title, body: t.home.benefit3Body, tag: t.editorial.benefitsTags[2] },
  ];
  return (
    <section className="border-t border-border" data-testid="benefits-section">
      <div className="container-wide py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="eyebrow">01 — {t.home.benefitsEyebrow}</p>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] font-medium text-foreground leading-[1.05]">
              {t.home.benefitsTitle}
            </h2>
          </div>
          <ol className="lg:col-span-8 divide-y divide-border border-y border-border">
            {items.map((it, i) => (
              <li key={i} className="group grid md:grid-cols-12 gap-6 py-8 md:py-10 items-start transition-colors hover:bg-secondary/30">
                <div className="md:col-span-1">
                  <span className="font-display text-3xl text-accent leading-none">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <div className="md:col-span-3 text-xs uppercase tracking-[0.22em] text-muted-foreground font-bold mt-2">
                  {it.tag}
                </div>
                <div className="md:col-span-8">
                  <h3 className="text-xl md:text-2xl text-foreground tracking-tight">{it.title}</h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed max-w-2xl">{it.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Process — vertical editorial timeline ---------------- */
function Process() {
  const { t } = useI18n();
  const steps = [
    { title: t.home.step1Title, body: t.home.step1Body, label: t.editorial.processSteps[0] },
    { title: t.home.step2Title, body: t.home.step2Body, label: t.editorial.processSteps[1] },
    { title: t.home.step3Title, body: t.home.step3Body, label: t.editorial.processSteps[2] },
    { title: t.home.step4Title, body: t.home.step4Body, label: t.editorial.processSteps[3] },
  ];
  return (
    <section className="border-t border-border bg-secondary/40" data-testid="process-section">
      <div className="container-wide py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <p className="eyebrow">02 — {t.home.processEyebrow}</p>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] font-medium text-foreground leading-[1.05]">
              {t.home.processTitle}
            </h2>
          </div>
          <div className="lg:col-span-5 text-muted-foreground text-sm leading-relaxed lg:text-right">
            {t.editorial.processSidekick}
          </div>
        </div>

        <ol className="mt-14 border-t border-border">
          {steps.map((s, i) => (
            <li
              key={i}
              className="group grid md:grid-cols-12 gap-6 py-10 md:py-12 border-b border-border items-baseline"
            >
              <div className="md:col-span-2 flex items-baseline gap-3">
                <span className="font-display text-4xl md:text-5xl text-foreground leading-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-accent">/</span>
              </div>
              <div className="md:col-span-3 text-xs uppercase tracking-[0.22em] text-accent font-bold">
                {s.label}
              </div>
              <div className="md:col-span-7">
                <h3 className="text-2xl md:text-3xl text-foreground tracking-tight">{s.title}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed max-w-2xl">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ---------------- Audiences — editorial duo with single hero image ---------------- */
function Audiences() {
  const { t } = useI18n();
  return (
    <section className="border-t border-border" data-testid="audiences-section">
      <div className="container-wide py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <p className="eyebrow">03 — {t.home.audienceEyebrow}</p>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] font-medium text-foreground leading-[1.05]">
              {t.home.audienceTitle}
            </h2>
          </div>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-x-10 gap-y-14">
          <Link
            to="/expat-housing"
            data-testid="audience-expat"
            className="group lg:col-span-7"
          >
            <div className="relative aspect-[16/10] overflow-hidden border border-border">
              <img
                src={EXPAT_IMG}
                alt="Expat housing — Amsterdam canal houses"
                className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]"
              />
              <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.25em] font-bold text-white/90 bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-sm">
                {t.editorial.audiencesFigExpat}
              </span>
            </div>
            <div className="mt-6 flex items-start justify-between gap-6">
              <h3 className="text-2xl md:text-3xl text-foreground tracking-tight max-w-xl leading-snug">
                {t.home.audienceExpat}.<br />
                <span className="text-muted-foreground text-lg md:text-xl">{t.home.audienceExpatBody}</span>
              </h3>
              <span className="mt-2 h-10 w-10 rounded-full border border-border grid place-items-center text-foreground/70 transition-colors group-hover:border-accent group-hover:text-accent flex-shrink-0">
                <ArrowUpRight className="h-4 w-4" />
              </span>
            </div>
          </Link>

          <div className="lg:col-span-5 grid gap-10 self-start">
            <Link to="/bedrijfspanden" className="group" data-testid="audience-hr">
              <p className="text-xs uppercase tracking-[0.22em] text-accent font-bold">{t.editorial.audiencesHrLabel}</p>
              <h3 className="mt-3 text-xl md:text-2xl text-foreground tracking-tight leading-snug">{t.home.audienceHR}</h3>
              <p className="mt-3 text-muted-foreground leading-relaxed">{t.home.audienceHRBody}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-foreground/70 group-hover:text-accent transition-colors">
                {t.common.learn_more} <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
            <div className="h-px bg-border" />
            <Link to="/bedrijfspanden" className="group" data-testid="audience-biz">
              <p className="text-xs uppercase tracking-[0.22em] text-accent font-bold">{t.editorial.audiencesBizLabel}</p>
              <h3 className="mt-3 text-xl md:text-2xl text-foreground tracking-tight leading-snug">{t.home.audienceBiz}</h3>
              <p className="mt-3 text-muted-foreground leading-relaxed">{t.home.audienceBizBody}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-foreground/70 group-hover:text-accent transition-colors">
                {t.common.learn_more} <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Pricing preview — restrained ---------------- */
function PricingPreview() {
  const { t } = useI18n();
  return (
    <section className="border-t border-border bg-secondary/40" data-testid="pricing-preview">
      <div className="container-wide py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <p className="eyebrow">04 — {t.home.pricingEyebrow}</p>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] font-medium text-foreground leading-[1.05]">
              {t.home.pricingTitle}
            </h2>
            <p className="mt-5 max-w-2xl text-muted-foreground leading-relaxed">{t.home.pricingSub}</p>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end">
            <Link to="/tarieven" className="btn-primary" data-testid="pricing-preview-cta">
              {t.home.seePricing} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <div className="mt-14 grid md:grid-cols-2 border-y border-border">
          <div className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-border bg-background">
            <p className="text-xs uppercase tracking-[0.22em] text-accent font-bold">{t.editorial.pricingExpatLabel}</p>
            <p className="mt-1 text-xs uppercase tracking-[0.18em] text-muted-foreground">{t.editorial.pricingExpatSub}</p>
            <p className="mt-6 font-display text-5xl md:text-6xl text-foreground tracking-[-0.02em]">
              <span className="text-muted-foreground text-base align-top mr-2">{t.common.from}</span>€250
            </p>
            <p className="mt-1 text-sm text-muted-foreground">{t.common.excl_vat}</p>
            <ul className="mt-8 space-y-3 text-sm text-foreground/85">
              <li className="flex items-baseline justify-between gap-6 border-b border-border pb-3">
                <span>{t.editorial.pricingExpatRow1}</span><span className="text-muted-foreground">€250</span>
              </li>
              <li className="flex items-baseline justify-between gap-6 border-b border-border pb-3">
                <span>{t.editorial.pricingRowSearch}</span><span className="text-muted-foreground">€750</span>
              </li>
              <li className="flex items-baseline justify-between gap-6">
                <span>{t.editorial.pricingExpatRow3}</span><span className="text-muted-foreground">{t.editorial.pricingExpatRow3Val}</span>
              </li>
            </ul>
          </div>
          <div className="p-8 md:p-10 bg-background">
            <p className="text-xs uppercase tracking-[0.22em] text-accent font-bold">{t.editorial.pricingBusinessLabel}</p>
            <p className="mt-1 text-xs uppercase tracking-[0.18em] text-muted-foreground">{t.editorial.pricingBusinessSub}</p>
            <p className="mt-6 font-display text-5xl md:text-6xl text-foreground tracking-[-0.02em]">
              <span className="text-muted-foreground text-base align-top mr-2">{t.common.from}</span>€500
            </p>
            <p className="mt-1 text-sm text-muted-foreground">{t.common.excl_vat}</p>
            <ul className="mt-8 space-y-3 text-sm text-foreground/85">
              <li className="flex items-baseline justify-between gap-6 border-b border-border pb-3">
                <span>{t.editorial.pricingBusinessRow1}</span><span className="text-muted-foreground">€500</span>
              </li>
              <li className="flex items-baseline justify-between gap-6 border-b border-border pb-3">
                <span>{t.editorial.pricingRowSearch}</span><span className="text-muted-foreground">€1.000</span>
              </li>
              <li className="flex items-baseline justify-between gap-6">
                <span>{t.editorial.pricingBusinessRow3}</span><span className="text-muted-foreground">€1.500 – €2.500 / 0,5–1%</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Testimonials — editorial pull quote ---------------- */
function Testimonials() {
  const { t } = useI18n();
  const items = [
    { body: t.home.t1, name: t.home.t1Name, role: t.home.t1Role },
    { body: t.home.t2, name: t.home.t2Name, role: t.home.t2Role },
    { body: t.home.t3, name: t.home.t3Name, role: t.home.t3Role },
  ];
  return (
    <section className="border-t border-border" data-testid="testimonials-section">
      <div className="container-wide py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <p className="eyebrow">05 — {t.home.testimonialsEyebrow}</p>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] font-medium text-foreground leading-[1.05]">
              {t.home.testimonialsTitle}
            </h2>
          </div>
        </div>

        <div className="mt-14 grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border border-y border-border">
          {items.map((it, i) => (
            <figure key={i} className="p-8 md:p-10 flex flex-col gap-6 group">
              <span className="font-display text-5xl text-accent leading-none">"</span>
              <blockquote className="text-lg text-foreground leading-relaxed font-display">
                {it.body}
              </blockquote>
              <figcaption className="mt-auto pt-6 border-t border-border">
                <p className="text-sm font-medium text-foreground">{it.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{it.role}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- FAQ preview — hairline accordion ---------------- */
function FaqPreview() {
  const { t } = useI18n();
  const items = t.faq.items.slice(0, 4);
  return (
    <section className="border-t border-border bg-secondary/40" data-testid="faq-preview-section">
      <div className="container-wide py-20 md:py-28 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-4">
          <p className="eyebrow">06 — {t.home.faqEyebrow}</p>
          <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] font-medium text-foreground leading-[1.05]">
            {t.home.faqTitle}
          </h2>
          <Link to="/faq" className="mt-6 inline-flex items-center gap-1.5 text-sm text-accent link-underline">
            {t.common.learn_more} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="lg:col-span-8 border-y border-border">
          {items.map((it, i) => (
            <details key={i} className="group py-7 border-b border-border last:border-b-0" data-testid={`faq-preview-${i}`}>
              <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                <span className="text-xl md:text-2xl text-foreground tracking-tight">
                  <span className="text-accent mr-3">{String(i + 1).padStart(2, "0")}</span>
                  {it.q}
                </span>
                <span className="mt-1.5 h-7 w-7 rounded-full border border-border grid place-items-center text-foreground/70 group-open:bg-accent group-open:text-accent-foreground group-open:border-accent transition-colors flex-shrink-0">
                  <Plus className="h-3.5 w-3.5 transition-transform group-open:rotate-45" />
                </span>
              </summary>
              <p className="mt-4 text-muted-foreground leading-relaxed pr-12 md:pl-10">{it.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Final CTA — editorial dark band ---------------- */
function CtaBand() {
  const { t } = useI18n();
  return (
    <section className="border-t border-border" data-testid="final-cta-section">
      <div className="bg-primary text-primary-foreground">
        <div className="container-wide py-20 md:py-28 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-accent">{t.editorial.ctaEyebrow}</p>
            <h2 className="mt-5 text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] font-medium leading-[1.02]">
              {t.home.ctaTitle}
            </h2>
            <p className="mt-5 text-primary-foreground/75 max-w-2xl leading-relaxed">{t.home.ctaSub}</p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link to="/contact?intent=intake" className="btn-accent" data-testid="final-cta-button">
              {t.nav.bookIntake} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { t, lang } = useI18n();
  useSeo({ title: t.seo.home.title, description: t.seo.home.description, path: "/", lang, image: HERO_IMG });
  return (
    <>
      <Hero />
      <Marquee />
      <Manifesto />
      <Benefits />
      <Process />
      <Audiences />
      <CasesPreview />
      <PricingPreview />
      <Testimonials />
      <FaqPreview />
      <CtaBand />
    </>
  );
}
